import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Loader, SdirCheckbox, SdirModal, Textarea } from '@sdir/sds';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import { AttachmentType } from '@sdir/httpclient/lib/clients/aps/application';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import {
  useAddedQualifications,
  useAttachmentGroupWorkspace,
  useCurrentApplication,
  useCurrentPerson,
  useFeatureToggles,
  useGetCertificateName,
  useSubmitSequence,
} from '../../../services/hooks';
import { AddedQualifications } from '../../../types';
import { FileSelect, QualificationBaseCard } from '../../Molecules';
import { ApplicationLayout } from '../../Organisms/ApplicationLayout';
import { routes } from '../../../constants/routes';
import { overruleApi, qualificationsApi } from '../../../httpclient';
import { qualificationIsApproved, qualificationIsRejected } from '../../../helpers/Qualifications';
import { sortListByAttribute } from '../../../helpers/sortHelpers';
import {
  determineQualificationCardStatus,
  QualificationBaseHeader,
  QualificationCardStatus,
} from '../../Molecules/QualificationBaseCard/QualificationBaseCard';
import { StyledTextLink } from '../../Organisms/NewAppStartFlow/Qualification/Qualification';
import {
  determineAlternativeStatus,
  QualificationAlternativeCard,
} from '../../Molecules/QualificationAlternativeCard/QualificationAlternativeCard';
import EditApplicationGuard from '../../Molecules/EditApplicationGuard/EditApplicationGuard';
import {
  CreateAttachmentGroupTracker,
  buildCreateAttachmentGroupSequence,
} from '../../../helpers/attachmentApiHelpers';
import { ToggleDisableNewSeagoing } from '../../../constants/permissions';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';

const MissingDocumentationPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const [showAllAlternatives, setShowAllAlternatives] = useState<boolean>(false);
  const { application, qualifications, refresh } = useCurrentApplication({
    includeQualifications: true,
  });
  const { addedSeagoing, addedNewSeagoing } = useAddedQualifications({ all: true });
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<Error | null | string>();
  const [overruleComment, setOverruleComment] = useState('');
  const [overruleChecked, setOverruleChecked] = useState(false);
  const [showOverruleValidation, setShowOverruleValidation] = useState(false);

  const isSid = ['SID', 'SID_duplicate'].includes(application?.result?.certificate || '');
  const heading = useGetCertificateName(application.result?.certificate || '');
  const featureToggle = useFeatureToggles();
  const {
    person: { result: currentPerson },
  } = useCurrentPerson();

  const enableNewSeagoing = !isToggleOn(
    featureToggle?.result,
    ToggleDisableNewSeagoing,
    currentPerson?.personalIdentificationNumber || ''
  );

  const {
    loading: overruleLoading,
    errorObject: overruleError,
    callApi: saveOverrule,
  } = useApi({
    apiFunction: (payload) => overruleApi.v1OverruleAttachCommandPut(payload),
    callApiOnInit: false,
  });

  const { availableFiles, changes, handleAddNewFiles, handleRemoveActiveFile } =
    useAttachmentGroupWorkspace('new');

  const addedQualifications: AddedQualifications = useMemo(
    () => ({
      seagoing: addedSeagoing.result || [],
      newSeagoing: addedNewSeagoing.result || {},
    }),
    [addedSeagoing.result, addedNewSeagoing.result]
  );

  const orderedBaseItems = useMemo(() => {
    return sortListByAttribute(qualifications?.result?.base?.items || [], 'uri');
  }, [qualifications?.result?.base?.items]);

  const { anyAlternativeOk, anyAlternativeOkSinceBefore } = useMemo(() => {
    let isAnyOk = false;
    let isAnyOkSinceBefore = false;
    if (qualifications?.result?.alternatives?.length === 0) isAnyOk = true;
    qualifications?.result?.alternatives?.forEach((a, i) => {
      const status: QualificationCardStatus = determineAlternativeStatus(
        false,
        a,
        addedQualifications,
        application.result?.attachments || [],
        application.result?.confirmedQualifications || []
      );
      if (a.status === true) isAnyOkSinceBefore = true;
      if (status === QualificationCardStatus.Valid) isAnyOk = true;
    });
    return {
      anyAlternativeOk: isAnyOk,
      anyAlternativeOkSinceBefore: isAnyOkSinceBefore,
    };
  }, [qualifications?.result?.alternatives, addedQualifications, application.result?.attachments]);

  const { allBaseItemsOk, allBaseItemsOkSinceBefore, anyBaseItemApprovedSinceBefore } =
    useMemo(() => {
      let allValid = true;
      let countItemsOkBefore = 0;
      qualifications?.result?.base?.items?.forEach((i) => {
        if (
          qualificationIsRejected(i, addedQualifications, application?.result?.attachments || [])
        ) {
          allValid = false;
        }
        const isApproved = qualificationIsApproved(
          i,
          addedQualifications,
          application?.result?.attachments || [],
          application?.result?.confirmedQualifications || []
        );
        if (isApproved) countItemsOkBefore += 1;
      });
      return {
        allBaseItemsOk: allValid,
        allBaseItemsOkSinceBefore: !!(
          countItemsOkBefore === qualifications?.result?.base?.items?.length
        ),
        anyBaseItemApprovedSinceBefore: countItemsOkBefore > 0,
      };
    }, [
      qualifications?.result?.base?.items,
      addedQualifications,
      application?.result?.attachments,
    ]);

  const addedQualificationsLoading = addedSeagoing.loading;
  const showLoader =
    application.loading || qualifications.loading || addedQualificationsLoading || isSubmitting;

  const [submitAttachmentGroupTracker, setSubmitAttachmentGroupTracker] =
    useState<CreateAttachmentGroupTracker>({});
  /* eslint-enable @typescript-eslint/indent */
  const { submit: submitAttachmentGroup } = useSubmitSequence(buildCreateAttachmentGroupSequence);

  const submitOverrule = async (): Promise<any> => {
    try {
      if (!application?.result) return Promise.resolve(false);

      if (overruleComment?.length > 0) {
        setShowOverruleValidation(false);
        const res = await saveOverrule({
          applicationUid: application?.result?.uid,
          comment: overruleComment,
        });
        if (!res?.success) throw Promise.reject(res);
        const { newFiles } = changes;
        // Create new attachment group
        await submitAttachmentGroup(
          {
            application: application.result,
            attachmentType: AttachmentType.OverRule,
            files: newFiles,
            simulatorResultItemUri: null,
          },
          submitAttachmentGroupTracker
        );
        return Promise.resolve(true);
      }
      setShowOverruleValidation(true);
      return Promise.resolve(false);
    } catch (error) {
      console.log('error: ', error);
      throw new Error();
    }
  };

  const onAfterSubmit = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      let shouldContinue = true;
      if (overruleChecked) {
        const result = await submitOverrule();
        if (result === false) {
          shouldContinue = false;
          setIsSubmitting(false);
        }
      }
      if (shouldContinue) {
        document.documentElement.scrollTop = 0;
        qualificationsApi
          .v1QualificationsSetqualificationsupdatedQueryPost({
            applicationUid: application?.result?.uid || '',
          })
          .then(() => {
            setIsSubmitting(false);
            setShowConfirmationModal(true);
          })
          .catch((e) => {
            setIsSubmitting(false);
            setShowConfirmationModal(true);
            setSubmitError(e);
          });
      }
    } catch (e) {
      console.log('error: ', e);
    }
  };

  const onConfirmationModalOk = () => {
    setShowConfirmationModal(false);
    handleExit();
  };

  const onConfirmationModalCancel = () => {
    setShowConfirmationModal(false);
  };

  const handleExit = useCallback(() => {
    refresh?.();
    navigate(
      generatePath(routes.applicationBase, { applicationUid: application?.result?.uid || '' })
    );
  }, [navigate, application?.result?.uid]);

  useEffect(() => {
    refresh?.();
  }, []);

  const showAlternativeSection =
    !!qualifications.result?.alternatives?.length && application.result;
  const showPreviouslyApproved =
    qualifications.result && application.result && anyBaseItemApprovedSinceBefore;
  const showBaseItems = qualifications.result && application.result && !allBaseItemsOkSinceBefore;
  const showErrorMessage = !overruleChecked && (!allBaseItemsOk || !anyAlternativeOk);

  return (
    <EditApplicationGuard>
      <ApplicationLayout
        topHeading={intl.formatMessage({ id: 'select.certificate.h1' })}
        heading={heading}
        hideHeading={false}
        sideContent={null}
        exitLabel={intl.formatMessage({ id: 'general.form.back' })}
        navBackLabel={intl.formatMessage({ id: 'general.form.cancel' })}
        navNextLabel={intl.formatMessage({ id: 'general.button.send' })}
        canNavNext={!isSubmitting && !overruleLoading}
        canNavBack
        hideNavigation={false}
        onNavBackClick={handleExit}
        onNavNextClick={onAfterSubmit}
        timeline={null}
      >
        <Wrapper>
          {showLoader ? (
            <Loader />
          ) : (
            <>
              {showPreviouslyApproved && (
                <>
                  <H2>{intl.formatMessage({ id: 'missingdocumentation.page.base.title3' })}</H2>
                  <PreviouslyApproved>
                    {qualifications.result.base?.items?.map((q, i) => {
                      const isApproved = qualificationIsApproved(
                        q,
                        addedQualifications,
                        application?.result?.attachments || [],
                        application?.result?.confirmedQualifications || []
                      );
                      if (isApproved) {
                        return (
                          <QualificationBaseHeader key={q?.uri} qualification={q} status="valid" />
                        );
                      }
                      return undefined;
                    })}
                  </PreviouslyApproved>
                </>
              )}
              {showBaseItems && (
                <>
                  <H2>
                    {allBaseItemsOk
                      ? intl.formatMessage({ id: 'missingdocumentation.page.base.title2' })
                      : intl.formatMessage({ id: 'missingdocumentation.page.base.title' })}
                    :
                  </H2>
                  <QualificationType>
                    {orderedBaseItems?.map((q) => {
                      const isApproved = qualificationIsApproved(
                        q,
                        addedQualifications,
                        application?.result?.attachments || [],
                        application?.result?.confirmedQualifications || []
                      );
                      if (isApproved) return undefined;
                      const status: QualificationCardStatus = determineQualificationCardStatus(
                        false,
                        q,
                        addedQualifications,
                        application?.result?.attachments || [],
                        application?.result?.confirmedQualifications || []
                      );
                      return (
                        <QualificationBaseCard
                          key={q.uri}
                          qualification={q}
                          addedQualifications={addedQualifications}
                          cardStatus={status}
                        />
                      );
                    })}
                  </QualificationType>
                </>
              )}
              {showAlternativeSection && (
                <>
                  <H2>
                    {anyAlternativeOk && !showAllAlternatives ? (
                      <>
                        {intl.formatMessage({
                          id: 'missingdocumentation.page.alternative.titlefulfilled',
                        })}
                        :
                      </>
                    ) : (
                      <FormattedMessage
                        id={
                          !showBaseItems
                            ? 'missingdocumentation.page.alternative.title2'
                            : 'missingdocumentation.page.alternative.title'
                        }
                        values={{ b: (chunks) => <b>{chunks}</b> }}
                      />
                    )}
                    {anyAlternativeOk && (
                      <StyledTextLink
                        text={intl.formatMessage({
                          id: showAllAlternatives
                            ? 'missingdocumentation.page.togglealternatives2'
                            : 'missingdocumentation.page.togglealternatives',
                        })}
                        size="16px"
                        click={() => setShowAllAlternatives(!showAllAlternatives)}
                      />
                    )}
                  </H2>
                  <AlternativesWrapper fullWidth={isSid}>
                    {qualifications?.result?.alternatives?.map((a, i) => {
                      const status: QualificationCardStatus = determineAlternativeStatus(
                        false,
                        a,
                        addedQualifications,
                        application.result?.attachments || [],
                        application.result?.confirmedQualifications || []
                      );
                      const hideAlternative =
                        anyAlternativeOk &&
                        !showAllAlternatives &&
                        status !== QualificationCardStatus.Valid;
                      if (hideAlternative) return <React.Fragment key={i} />;
                      return (
                        <QualificationAlternativeCard
                          number={i + 1}
                          key={i}
                          application={application.result}
                          qualifications={a}
                          addedQualifications={addedQualifications}
                          cardStatus={status}
                          enableNewSeagoing={enableNewSeagoing}
                        />
                      );
                    })}
                  </AlternativesWrapper>
                </>
              )}
              {!showLoader && qualifications.result && application.result && !isSid && (
                <Overrule>
                  <h3>{intl.formatMessage({ id: 'qualification.overrule.title' })}</h3>
                  <SdirCheckbox
                    checked={overruleChecked}
                    title={intl.formatMessage({ id: 'qualification.overrule.toggle' })}
                    onClick={() => setOverruleChecked(!overruleChecked)}
                  />
                  <OverruleFields>
                    {overruleChecked && (
                      <>
                        <OverruleComment>
                          <Textarea
                            placeholder={intl.formatMessage({
                              id: 'qualification.overrule.comment',
                            })}
                            contentValue={overruleComment}
                            onChange={(e) => {
                              const value = e?.target?.value;
                              setOverruleComment(value);
                              if (value) {
                                setShowOverruleValidation(false);
                              }
                            }}
                            error={
                              showOverruleValidation
                                ? intl.formatMessage({ id: 'general.form.error' })
                                : undefined
                            }
                          />
                        </OverruleComment>
                        <FileSelect
                          availableFiles={availableFiles}
                          selectedFileOption={null}
                          onSelectFile={() => {}}
                          onNewFilesAdded={handleAddNewFiles}
                          onDeleteFile={handleRemoveActiveFile}
                          deleteAny
                          showWhenEmpty
                        />
                      </>
                    )}
                  </OverruleFields>
                  {overruleError && <ErrorMessage error={overruleError} />}
                </Overrule>
              )}
              {showErrorMessage && (
                <ErrorWrapper>
                  <ErrorMessage
                    title={intl.formatMessage({ id: 'app.requirements.missing.error.title' })}
                    error={intl.formatMessage({ id: 'app.requirements.missing.error.desc' })}
                  />
                </ErrorWrapper>
              )}
            </>
          )}
          <StyledConfirmModal
            visible={showConfirmationModal}
            onOK={onConfirmationModalOk}
            okText={submitError ? undefined : 'OK'}
            onCancel={onConfirmationModalCancel}
            cancelText={submitError ? intl.formatMessage({ id: 'general.form.close' }) : undefined}
          >
            {submitError ? (
              <ErrorMessage
                title={intl.formatMessage({ id: 'app.requirements.state.update.error' })}
                error={submitError}
              />
            ) : (
              <>
                <h2>
                  {intl.formatMessage({
                    id: 'missingdocumentation.page.confirmmodal.receivedtitle',
                  })}
                </h2>
                <div>
                  {intl.formatMessage({
                    id: 'missingdocumentation.page.confirmmodal.receivedinfo',
                  })}
                </div>
              </>
            )}
          </StyledConfirmModal>
        </Wrapper>
      </ApplicationLayout>
    </EditApplicationGuard>
  );
};

const Wrapper = styled.div`
  width: 100%;
`;

const H2 = styled.h2`
  font-size: 24px;
`;

const StyledConfirmModal = styled(SdirModal)`
  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    & > div {
      padding: 6rem;
      min-width: 120px;
      max-width: 320px;
    }
  }
`;

const PreviouslyApproved = styled.div`
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;

  & > div {
    margin-bottom: 10px;
  }
`;

const ErrorWrapper = styled.div`
  max-width: 600px;
  margin-left: auto;
  margin-top: 20px;
`;

const Overrule = styled.div``;
const OverruleFields = styled.div`
  margin-bottom: 20px;
`;
const OverruleComment = styled.div`
  max-width: 600px;
  margin: 20px 0;
`;

const QualificationType = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-row-gap: 4rem;
  grid-column-gap: 3rem;
  justify-items: stretch;
  margin: 2rem 0 6rem 0;

  @media screen and (max-width: ${({ theme }) => theme.screen.xLarge}) {
    grid-template-columns: 1fr 1fr 1fr;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.large}) {
    grid-template-columns: 1fr 1fr;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    grid-template-columns: 1fr;
  }
`;

const AlternativesWrapper = styled.div<{ fullWidth?: boolean }>`
  margin: 16px 0 32px 0;
  display: grid;
  grid-row-gap: 4rem;
  grid-column-gap: 3rem;
  grid-template-columns: 1fr 1fr 1fr 1fr;

  @media screen and (max-width: ${({ theme }) => theme.screen.xLarge}) {
    grid-template-columns: 1fr 1fr 1fr;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.large}) {
    grid-template-columns: 1fr 1fr;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    grid-template-columns: 1fr;
  }

  ${({ fullWidth }) =>
    fullWidth
      ? css`
          grid-template-columns: 1fr !important;
        `
      : css`
          grid-template-columns: 1fr 1fr 1fr 1fr;
        `}
`;

export default MissingDocumentationPage;
